import { SessionProps } from 'components/Analytics/google-analytics-store';
import { PromoCode } from 'redux/modules/order/state/initial-state';

// GTM Enhanced Ecommerce Types
export interface GtmProduct {
  id: string;
  name: string;
  category?: string[];
  variant?: string;
  brand: string;
  favourite: 'YES' | 'NO';
  quantity: number;
  price: number;
  amendment_quantity_delta?: number;
  offer?: string;
  sponsored?: string;
  sponsored_source?: string;
}

export interface GtmActionField {
  id: string;
  affiliation?: string;
  revenue: number;
  shipping: number;
  coupon?: string;
  option?: string;
  service_type?: string;
  payment_type?: string;
  account_saved_card?: boolean;
  save_new_card?: string;
  place_order_origin?: string;
  place_order_origin_mobile?: string;
  slot_date?: string;
  slot_type?: string;
  slot_shipping_charge?: number;
  slot_duration?: string;
  slot_hour?: number;
  slot_time?: string;
  slot_dow?: string;
  order_event_date?: string;
  order_event_hour?: number;
  order_event_time?: string;
  order_event_dow?: string;
  order_event_slot_date_diff?: number;
}

export interface GtmPurchase {
  actionField: GtmActionField;
  products: GtmProduct[];
}

export interface GtmUser {
  customer_id?: string;
  customerId?: string;
  email_address?: string;
}

export type GtmPurchaseEvent = {
  event: 'transaction_completed';
  instant_checkout_eligibility: boolean;
  orderDate: string;
  user?: GtmUser;
  ecommerce?: {
    purchase?: GtmPurchase;
  };
};

export type GtmCheckoutReviewOrderEvent = {
  event: 'checkout_review_order';
  instant_checkout_eligibility: boolean;
  user?: GtmUser;
  ecommerce?: {
    checkout?: {
      actionField?: {
        option: string;
        id: string;
        service_type: string;
        account_saved_card: boolean;
      };
    };
  };
};

type GtmCheckoutClickConfirmActionField = {
  option: string;
  payment_type: string;
};

export type GtmCheckoutClickConfirmEvent = {
  event: 'checkout_click_confirm';
  instant_checkout_eligibility: boolean;
  ecommerce?: {
    checkout?: {
      actionField?: GtmCheckoutClickConfirmActionField;
    };
  };
};

export type GtmRedeemGiftCardEvent = {
  event: 'redeem_gift_card';
  gift_card_number: string;
};

export type GtmRedeemGiftVoucherEvent = {
  event: 'redeem_gift_voucher';
  gift_voucher_number: string;
};

export type GtmRedeemPromoCodeEvent = {
  event: 'redeem_promo_code';
  promo_code: string;
};

export type GtmPromoCodeAddedEvent = {
  event: 'promo_code_added';
  customerId: string;
  promoCode: {
    result: PromoCode;
  };
  status: string;
};

export type GtmPromoCodeRemovedEvent = {
  event: 'promo_code_removed';
  customerId: string;
  promoCode: PromoCode;
  result: unknown;
};

export type GtmPromoCodeApiErrorEvent = {
  event: 'promo_code_api_error';
  method: 'POST' | 'DELETE';
  customerId: string;
  error: {
    status: number;
    message: string;
  };
};

export type GtmPromoCodeAddApiErrorEvent = GtmPromoCodeApiErrorEvent & {
  promoCode: string;
  method: 'POST';
};

export type GtmPromoCodeRemoveApiErrorEvent = GtmPromoCodeApiErrorEvent & {
  promoCode: PromoCode;
  method: 'DELETE';
};

export type GtmMonetateFeExperimentPageLoadEvent = {
  event: 'monetate_fe_experiment_page_load';
  monetate_fe_experiment_page_load?: {
    id: string;
    variant: string;
    experience_type: string;
    experience_id: number;
    experience_name: string;
    experience_label: string;
    variant_label: string;
    control_allocation: number;
  }[];
};

export type GtmMonetateFeExperimentViewedEvent = {
  event: 'monetate_fe_experiment_viewed';
  monetate_fe_experiment: {
    id: string;
    variant: string;
    experience_type: string;
    experience_id: number;
    experience_name: string;
    experience_label: string;
    variant_label: string;
    control_allocation: number;
  };
};

export type GtmModalOpenedEvent = {
  event: 'modal_opened';
  customerId: string;
  modal: {
    id: string;
    severity: string;
    title: string;
  };
};

export type GtmModalClosedEvent = {
  event: 'modal_closed';
  customerId: string;
  modal: {
    id: string;
    severity: string;
    title: string;
    response: string;
  };
};

export type GtmNotificationOpenedEvent = {
  event: 'notification_opened';
  notificationId: string;
};

export type GtmNotificationClickedEvent = {
  event: 'notification_clicked';
  notificationId: string;
  notificationCtaLabel?: string;
};

export type GtmNotificationClosedEvent = {
  event: 'notification_closed';
  notificationId: string;
};

export type GtmRemoveVoucherFromOrderEvent = {
  event: 'remove_voucher_from_order';
  remove_voucher: {
    voucher_id: string;
    product_id: string;
    category_name: string;
  };
};

interface EventValue {
  [key: string]: EventValue | EventValue[] | string | number | boolean | undefined | null;
}

type EventValuePrimitive = EventValue | EventValue[] | string | number | boolean | undefined | null;

export type GtmGenericEvent = {
  event: string;
  [key: string]: EventValuePrimitive;
};

// GA4 Types
export interface Ga4PurchaseItem extends EventValue {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_variant: string;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  price: number;
  quantity: number;
  amendment_quantity_delta?: number;
  item_favourite: 'YES' | 'NO';
  item_offer: string;
  item_sponsored: string;
  item_sponsored_source: string;
}

export type Ga4EventParams = {
  event_name: string;
  [key: string]: EventValuePrimitive;
};

export type Ga4PurchaseEventParams = Ga4EventParams & {
  event_name: 'purchase';
  value: number;
  order_date: string;
  shipping: number;
  payment_type: string;
  service_type: string;
  coupon: string;
  save_new_card: string;
  account_saved_card: boolean;
  place_order_origin: string;
  place_order_origin_mobile: string;
  branch_id: string;
  purchase_or_amend: 'purchase' | 'amend';
  order_id: string;
  slot_date: string | undefined;
  slot_type: string | undefined;
  slot_shipping_charge: number | undefined;
  slot_duration: string | undefined;
  slot_hour: number | undefined;
  slot_time: string | undefined;
  slot_dow: string | undefined;
  order_event_date: string | undefined;
  order_event_hour: number | undefined;
  order_event_time: string | undefined;
  order_event_dow: string | undefined;
  order_event_slot_date_diff: number | undefined;
  items: Ga4PurchaseItem[];
};

/**
 * Converts GTM Enhanced Ecommerce purchase event to GA4 format
 * @param eventName - The GTM event name
 * @param gtmEventParams - The GTM event
 * @returns GA4 formatted event including the `event_name`
 */
export function convertGtmEventToGa4EventParams(
  gtmEvent: GtmGenericEvent | GtmPurchaseEvent,
  sessionProps: SessionProps,
): Ga4EventParams {
  switch (gtmEvent.event) {
    case 'virtual_page_view': {
      return {
        event_name: 'page_view',
      };
    }

    case 'monetate_fe_experiment_page_load': {
      const monetateFeExperimentPageLoadEvent = gtmEvent as GtmMonetateFeExperimentPageLoadEvent;
      return {
        event_name: 'monetate_fe_experiment_page_load',
        monetate_fe_experiment_page_load:
          monetateFeExperimentPageLoadEvent.monetate_fe_experiment_page_load?.map(exp => ({
            id: exp.id,
            variant: exp.variant,
          })) || [],
      };
    }

    case 'monetate_fe_experiment_viewed': {
      const monetateFeExperimentViewedEvent = gtmEvent as GtmMonetateFeExperimentViewedEvent;
      return {
        event_name: 'monetate_experiment_viewed',
        id: monetateFeExperimentViewedEvent.monetate_fe_experiment?.id || '',
        variant: monetateFeExperimentViewedEvent.monetate_fe_experiment?.variant || '',
      };
    }

    case 'checkout_review_order': {
      const checkoutEvent = gtmEvent as unknown as GtmCheckoutReviewOrderEvent;
      return {
        event_name: 'checkout_review_order',
        purchase_or_amend: checkoutEvent.ecommerce?.checkout?.actionField?.option || '',
        instant_checkout_available: checkoutEvent.instant_checkout_eligibility || false,
        instant_checkout_eligibility: checkoutEvent.instant_checkout_eligibility || false,
        order_id: checkoutEvent.ecommerce?.checkout?.actionField?.id || '',
        customer_id: checkoutEvent.user?.customerId || '',
        user_id: checkoutEvent.user?.customerId || '',
      };
    }

    case 'modal_opened': {
      const modalOpenedEvent = gtmEvent as GtmModalOpenedEvent;
      return {
        event_name: 'modal_opened',
        modal_name: modalOpenedEvent.modal?.id || '',
      };
    }

    case 'modal_closed': {
      const modalClosedEvent = gtmEvent as GtmModalClosedEvent;
      return {
        event_name: 'close_modal',
        modal_name: modalClosedEvent.modal?.id || '',
        action: modalClosedEvent.modal?.response || '',
      };
    }

    case 'notification_opened': {
      const notificationOpenedEvent = gtmEvent as GtmNotificationOpenedEvent;
      return {
        event_name: 'notification_opened',
        notificationId: notificationOpenedEvent.notificationId || '',
      };
    }

    case 'notification_clicked': {
      const notificationClickedEvent = gtmEvent as GtmNotificationClickedEvent;
      return {
        event_name: 'notification_clicked',
        notificationId: notificationClickedEvent.notificationId || '',
        notificationCtaLabel: notificationClickedEvent.notificationCtaLabel || '',
      };
    }

    case 'notification_closed': {
      const notificationClosedEvent = gtmEvent as GtmNotificationClosedEvent;
      return {
        event_name: 'notification_closed',
        notificationId: notificationClosedEvent.notificationId || '',
      };
    }

    case 'remove_voucher_from_order': {
      const removeVoucherEvent = gtmEvent as GtmRemoveVoucherFromOrderEvent;
      return {
        event_name: 'remove_voucher_from_order',
        voucher_id: removeVoucherEvent.remove_voucher.voucher_id || '',
        product_id: removeVoucherEvent.remove_voucher.product_id || '',
        category_name: removeVoucherEvent.remove_voucher.category_name || '',
      };
    }

    case 'checkout_click_confirm': {
      const checkoutEvent = gtmEvent as GtmCheckoutClickConfirmEvent;
      const actionField = checkoutEvent.ecommerce?.checkout?.actionField;

      return {
        event_name: 'click_confirm_checkout',
        payment_type: actionField?.payment_type || '',
        service_type: sessionProps.service_type || '',
        purchase_or_amend: actionField?.option || '',
        instant_checkout_available: checkoutEvent.instant_checkout_eligibility || false,
        instant_checkout_eligibility: checkoutEvent.instant_checkout_eligibility || false,
      };
    }

    case 'redeem_gift_card': {
      const checkoutEvent = gtmEvent as GtmRedeemGiftCardEvent;

      return {
        event_name: 'redeem_gift_card',
        gift_card_number: checkoutEvent.gift_card_number || '',
        payment_type: sessionProps.payment_type || '',
        service_type: sessionProps.service_type || '',
      };
    }

    case 'redeem_gift_voucher': {
      const checkoutEvent = gtmEvent as GtmRedeemGiftVoucherEvent;

      return {
        event_name: 'redeem_gift_voucher',
        gift_voucher_number: checkoutEvent.gift_voucher_number || '',
        payment_type: sessionProps.payment_type || '',
        service_type: sessionProps.service_type || '',
      };
    }

    case 'redeem_promo_code': {
      const checkoutEvent = gtmEvent as GtmRedeemPromoCodeEvent;

      return {
        event_name: 'redeem_promo_code',
        promo_code: checkoutEvent.promo_code || '',
        payment_type: sessionProps.payment_type || '',
        service_type: sessionProps.service_type || '',
      };
    }

    case 'promo_code_added': {
      const promoCodeEvent = gtmEvent as unknown as GtmPromoCodeAddedEvent;

      return {
        event_name: 'promo_code_added',
        customer_id: promoCodeEvent.customerId || '',
        promo_code: promoCodeEvent.promoCode?.result?.code || '',
        promotion_name: promoCodeEvent.promoCode?.result?.marketingCampaignId || '',
        value: promoCodeEvent.promoCode?.result?.value?.amount || 0,
        currency: promoCodeEvent.promoCode?.result?.value?.currencyCode || 'GBP',
        status: promoCodeEvent.status || '',
        customerId: promoCodeEvent.customerId || '',
      };
    }

    case 'promo_code_removed': {
      const promoCodeEvent = gtmEvent as unknown as GtmPromoCodeRemovedEvent;

      return {
        event_name: 'promo_code_removed',
        customer_id: promoCodeEvent.customerId || '',
        promo_code: promoCodeEvent.promoCode?.code || '',
        promotion_name: promoCodeEvent.promoCode?.marketingCampaignId || '',
        value: promoCodeEvent.promoCode?.value?.amount || 0,
        currency: promoCodeEvent.promoCode?.value?.currencyCode || 'GBP',
        status: promoCodeEvent.promoCode?.status || '',
        customerId: promoCodeEvent.customerId || '',
      };
    }

    case 'promo_code_api_error': {
      const apiErrorEvent = gtmEvent as GtmPromoCodeApiErrorEvent;

      let promoCodeEvent: GtmPromoCodeAddApiErrorEvent | GtmPromoCodeRemoveApiErrorEvent;
      let errorType: string;
      let promoCode: PromoCode;
      if (apiErrorEvent.method === 'POST') {
        promoCodeEvent = apiErrorEvent as GtmPromoCodeAddApiErrorEvent;
        errorType = 'Add Promo Code';
        promoCode = { code: promoCodeEvent.promoCode } as PromoCode;
      } else {
        promoCodeEvent = apiErrorEvent as GtmPromoCodeRemoveApiErrorEvent;
        promoCode = promoCodeEvent.promoCode || {};
        errorType = 'Remove Promo Code';
      }

      return {
        event_name: 'promo_code_api_error',
        customer_id: apiErrorEvent.customerId || '',
        promo_code: promoCode.code || '',
        error_type: errorType,
        error_description: apiErrorEvent.error?.message || 'Unknown error',
        promotion_name: promoCode.marketingCampaignId || '',
        value: promoCode.value?.amount || 0,
        currency: promoCode.value?.currencyCode || 'GBP',
        errors: 1,
        status: promoCode.status || '',
      };
    }

    case 'transaction_completed': {
      const purchaseEvent = gtmEvent as GtmPurchaseEvent;
      const purchase = purchaseEvent.ecommerce?.purchase;
      const actionField = purchase?.actionField;
      const products = purchase?.products || [];

      const items: Ga4PurchaseItem[] = products.map(product => ({
        item_id: product.id,
        item_name: product.name,
        item_brand: product.brand,
        item_variant: product.variant || '',
        item_category: product.category?.[0] || '',
        item_category2: product.category?.[1] || '',
        item_category3: product.category?.[2] || '',
        item_category4: product.category?.[3] || '',
        price: product.price,
        quantity: product.quantity,
        ...(actionField?.option === 'amend'
          ? { amendment_quantity_delta: product.amendment_quantity_delta }
          : undefined),
        item_favourite: product.favourite === 'YES' ? 'YES' : 'NO',
        item_offer: product.offer || '',
        item_sponsored: product.sponsored || '',
        item_sponsored_source: product.sponsored_source || '',
      }));

      const purchaseOrAmend: 'purchase' | 'amend' =
        actionField?.option === 'amend' ? 'amend' : 'purchase';

      let value: number;
      if (actionField?.option === 'amend') {
        value = items.reduce((acc, item) => {
          if (item.amendment_quantity_delta) {
            return acc + item.price * item.amendment_quantity_delta;
          }
          return acc;
        }, 0);
      } else {
        value = actionField?.revenue || 0;
      }

      return {
        event_name: 'purchase',
        value,
        order_date: purchaseEvent.orderDate || '',
        shipping: actionField?.shipping || 0,
        payment_type: actionField?.payment_type || '',
        service_type: actionField?.service_type || '',
        coupon: actionField?.coupon || '',
        save_new_card: actionField?.save_new_card || '',
        account_saved_card: actionField?.account_saved_card || false,
        place_order_origin: actionField?.place_order_origin || '',
        place_order_origin_mobile: actionField?.place_order_origin_mobile || '',
        branch_id: actionField?.affiliation || '',
        purchase_or_amend: purchaseOrAmend,
        order_id: actionField?.id || '',
        slot_date: actionField?.slot_date,
        slot_type: actionField?.slot_type,
        slot_shipping_charge: actionField?.slot_shipping_charge,
        slot_duration: actionField?.slot_duration,
        slot_hour: actionField?.slot_hour,
        slot_time: actionField?.slot_time,
        slot_dow: actionField?.slot_dow,
        order_event_date: actionField?.order_event_date,
        order_event_hour: actionField?.order_event_hour,
        order_event_time: actionField?.order_event_time,
        order_event_dow: actionField?.order_event_dow,
        order_event_slot_date_diff: actionField?.order_event_slot_date_diff,
        items,
      } satisfies Ga4PurchaseEventParams;
    }

    default: {
      const { event: eventName, ...params } = gtmEvent as GtmGenericEvent;
      return {
        event_name: eventName,
        ...params,
      };
    }
  }
}
